import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { State } from "../../../reducer";
import { LayoutAdminCafe } from "../../../layout/layout_cafe";
import { getSettings, patchFridaySpecial } from "../../../api/settings.api";
import { uploadMenuImage } from "../../../api/image.api";
import { fileToResizedDataUrl, ikThumb } from "../../../utils/image";
import { isStaff } from "../../../utils/access";
import CropImageModal from "../../../components/CropImageModal";

function FridaySpecialInner() {
  const role = useSelector((s: State) => s.user?.role);
  const navigate = useNavigate();

  const [active, setActive] = useState(false);
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageFileId, setImageFileId] = useState<string>("");
  const [savedActive, setSavedActive] = useState(false);
  const [savedDescription, setSavedDescription] = useState("");
  const [savedImageUrl, setSavedImageUrl] = useState<string>("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (role && !isStaff(role)) {
      navigate("/unauthorized", { replace: true });
      return;
    }
    getSettings()
      .then((s) => {
        setActive(s.fridaySpecialActive);
        setDescription(s.fridaySpecialDescription ?? "");
        setImageUrl(s.fridaySpecialImage ?? "");
        setImageFileId(s.fridaySpecialImageFileId ?? "");
        setSavedActive(s.fridaySpecialActive);
        setSavedDescription(s.fridaySpecialDescription ?? "");
        setSavedImageUrl(s.fridaySpecialImage ?? "");
      })
      .finally(() => setLoading(false));
  }, [role, navigate]);

  const dirty =
    active !== savedActive
    || description !== savedDescription
    || imageUrl !== savedImageUrl;

  const handlePickFile = () => fileRef.current?.click();

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) {
      toast.error("Image too large to read (>30 MB)");
      return;
    }
    try {
      const base64 = await fileToResizedDataUrl(file, 2400, 0.95);
      setCropSrc(base64);
    } catch {
      toast.error("Could not read image");
    }
    e.target.value = "";
  };

  const handleCropConfirm = async (cropped: string) => {
    setCropSrc(null);
    setUploading(true);
    try {
      const result = await uploadMenuImage({
        file: cropped,
        replaceFileId: imageFileId || undefined,
      });
      if (result?.url) {
        setImageUrl(result.url);
        setImageFileId(result.fileId);
      } else {
        toast.error("Image upload failed");
      }
    } catch (err) {
      console.error(err);
      // Show why it failed (size, format, session) instead of a generic line.
      toast.error(err instanceof Error && err.message ? err.message : "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const fresh = await patchFridaySpecial({
        active,
        description: description.trim() || null,
        image: imageUrl || null,
        imageFileId: imageFileId || null,
      });
      setSavedActive(fresh.fridaySpecialActive);
      setSavedDescription(fresh.fridaySpecialDescription ?? "");
      setSavedImageUrl(fresh.fridaySpecialImage ?? "");
      toast.success("Friday Special saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setActive(savedActive);
    setDescription(savedDescription);
    setImageUrl(savedImageUrl);
  };

  return (
    <div className="min-h-screen bg-ink-50 font-sans pb-[140px]">
      <header className="relative bg-ink-1000 text-ink-0 px-5 pt-[28px] pb-[28px] overflow-hidden">
        <div
          className="absolute -right-12 -top-12 w-[200px] h-[200px] rounded-full opacity-[0.08] pointer-events-none"
          style={{ background: "var(--festive-1, #ffffff)" }}
        />
        <div className="absolute right-6 top-1/2 -translate-y-1/2 text-5xl opacity-20" aria-hidden>✨</div>
        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-ink-0/60 mb-1">Friday only</p>
        <h1 className="font-display font-black text-[32px] leading-tight tracking-tightest">
          Friday Special
        </h1>
        <p className="text-sm text-ink-0/70 mt-2 max-w-md">
          Banner shown when staff log in on Fridays. Right now only{" "}
          <span className="font-bold text-ink-0">dev</span> sees it — flip a switch in code to roll out.
        </p>
      </header>

      <main className="px-5 pt-[20px] space-y-3 max-w-2xl mx-auto">
        {loading ? (
          <div className="py-[80px] flex justify-center">
            <svg className="w-7 h-7 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
              <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
            </svg>
          </div>
        ) : (
          <>
            {/* Active toggle */}
            <section className="rounded-3xl border border-ink-100 bg-white shadow-soft p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="font-display font-black text-base text-ink-1000">Banner active</p>
                  <p className="text-xs text-ink-500 mt-0.5">
                    Off = no banner shown even on Fridays.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  aria-label="Banner active"
                  onClick={() => setActive(!active)}
                  className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-1000 focus-visible:ring-offset-2 ${
                    active ? "bg-emerald-500" : "bg-ink-300"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform duration-150 ease-out ${
                      active ? "translate-x-7" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </section>

            {/* Image */}
            <section className="rounded-3xl border border-ink-100 bg-white shadow-soft p-5">
              <p className="font-display font-black text-base text-ink-1000">Image</p>
              <p className="text-xs text-ink-500 mt-0.5 mb-4">
                A square photo of the special menu. Cropped to 1:1 to match the modal.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={handlePickFile}
                disabled={uploading}
                className="relative block w-full aspect-square rounded-2xl border-2 border-dashed border-ink-200 bg-ink-50 overflow-hidden text-left disabled:opacity-50"
              >
                {imageUrl ? (
                  <img
                    src={imageUrl.startsWith("data:") ? imageUrl : ikThumb(imageUrl, 800)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-ink-500">
                    <span className="text-3xl" aria-hidden>📷</span>
                    <span className="text-xs font-semibold">Tap to upload</span>
                    <span className="text-[11px] text-ink-400">PNG · JPG · WEBP · auto-resized</span>
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                    <svg className="w-6 h-6 animate-spin text-ink-700" fill="none" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
                      <path d="M12 2a10 10 0 0110 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </div>
                )}
              </button>
              {imageUrl && !uploading && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={handlePickFile}
                    className="btn-v2-secondary btn-v2-sm flex-1"
                  >
                    Replace
                  </button>
                  <button
                    type="button"
                    onClick={() => { setImageUrl(""); setImageFileId(""); }}
                    className="btn-v2-secondary btn-v2-sm flex-1"
                  >
                    Remove
                  </button>
                </div>
              )}
            </section>

            {/* Description */}
            <section className="rounded-3xl border border-ink-100 bg-white shadow-soft p-5">
              <p className="font-display font-black text-base text-ink-1000">Description</p>
              <p className="text-xs text-ink-500 mt-0.5 mb-3">
                What's special this Friday? Keep it short — it appears on the modal.
              </p>
              <div className="relative">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={1000}
                  rows={5}
                  placeholder="e.g. This Friday's exclusive: Strawberry-matcha croissant with house-made vanilla cream — only 15 made, first come first served."
                  className="w-full px-4 py-3 rounded-2xl bg-ink-50 border-2 border-transparent focus:border-ink-1000 focus:bg-white focus:outline-none text-base text-ink-1000 placeholder:text-ink-400 resize-none transition-colors"
                />
                <span className="absolute right-3 bottom-3 text-[11px] text-ink-400">
                  {description.length}/1000
                </span>
              </div>
            </section>
          </>
        )}
      </main>

      {dirty && !loading && (
        <div
          className="fixed bottom-0 left-0 right-0 md:left-[280px] z-30 bg-white/95 backdrop-blur-md border-t border-ink-200 px-5 py-3 shadow-floaty animate-slide-up"
          style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
        >
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <p className="flex-1 min-w-0 text-xs text-ink-700 truncate">
              <span className="font-bold text-ink-1000">Unsaved changes</span>
            </p>
            <button
              type="button"
              onClick={handleReset}
              disabled={saving}
              className="btn-v2-secondary btn-v2-sm shrink-0"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="btn-v2-primary btn-v2-sm shrink-0 disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>
      )}

      {cropSrc && (
        <CropImageModal
          src={cropSrc}
          aspect={1}
          onCancel={() => setCropSrc(null)}
          onConfirm={handleCropConfirm}
        />
      )}

      <ToastContainer />
    </div>
  );
}

export default function FridaySpecialPage() {
  return (
    <LayoutAdminCafe>
      <FridaySpecialInner />
    </LayoutAdminCafe>
  );
}
